import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PaymentInstructionsModal from '../../components/brand/PaymentInstructionsModal';
import TermsAndConditionsModal from '../../components/common/TermsAndConditionsModal';
import TemplateSelector from '../../components/brand/TemplateSelector';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeHtml, sanitizeUrl, sanitizeObject, sanitizeContentGuidelines } from '../../security/sanitize';
import { formatBriefForDisplay } from '../../services/contentTemplatesService';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';
import { DatePicker } from '../../components/ui/date-picker';

const CreateCampaign = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stepErrors, setStepErrors] = useState({});
  const [brandStores, setBrandStores] = useState([]);
  const [campaignImage, setCampaignImage] = useState(null);
  const [campaignImagePreview, setCampaignImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  
  // Load brand stores from userProfile
  useEffect(() => {
    if (userProfile && userProfile.stores && Array.isArray(userProfile.stores)) {
      setBrandStores(userProfile.stores);

    } else {

      setBrandStores([]);
    }
  }, [userProfile]);
  
  // Toast notification system
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  const [commissionAmount, setCommissionAmount] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentInstructionsModal, setShowPaymentInstructionsModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAcceptedDate, setTermsAcceptedDate] = useState(null);
  const [createdCampaignId, setCreatedCampaignId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateBrief, setTemplateBrief] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    description: '',
    objectives: [],
    category: '',
    imageUrl: '',
    
    // Campaign Details
    startDate: '',
    applicationDeadline: '',
    reviewDeadline: '',
    endDate: '',
    budget: '',
    compensationType: 'monetary', // monetary or product
    compensationAmount: '',
    productDetails: '',
    
    // Influencer Requirements
    minFollowers: '',
    maxInfluencers: '',
    totalSpots: '', // Number of influencers needed for this campaign
    preferredNiches: [],
    requiredPlatforms: [],
    
    // Content Requirements
    contentType: [],
    contentGuidelines: '',
    requiredHashtags: '',
    requiredMentions: '',
    contentTemplateId: '',
    contentExamples: [], // Optional brand-provided examples
    
    // Additional Information
    additionalInfo: '',
    termsAndConditions: false,
    
    // Payment Information
    commissionAgreed: false,
    
    // Store Selection
    storeSelection: {
      type: 'all', // 'all', 'specific', 'regional'
      selectedStores: [],
      selectedRegions: [],
      excludedStores: []
    }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Calculate commission when budget changes
      if (name === 'budget') {
        const budgetValue = parseFloat(value.replace(/[^0-9.]/g, ''));
        if (!isNaN(budgetValue)) {
          const commission = budgetValue * 0.7; // 70% initial payment
          setCommissionAmount(commission);
        } else {
          setCommissionAmount(0);
        }
      }
    }
  };

  const handleArrayChange = (name, value) => {
    if (formData[name].includes(value)) {
      setFormData(prev => ({
        ...prev,
        [name]: prev[name].filter(item => item !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: [...prev[name], value]
      }));
    }
  };

  // Template selection handler
  const handleTemplateSelect = (templateId, brief) => {
    setSelectedTemplateId(templateId);
    setTemplateBrief(brief);
    
    if (templateId === 'custom') {
      // Custom brief - clear template data
      setFormData(prev => ({
        ...prev,
        contentTemplateId: 'custom',
        contentGuidelines: ''
      }));
    } else if (brief) {
      // Template selected - auto-fill content guidelines
      const formattedBrief = formatBriefForDisplay(brief);
      setFormData(prev => ({
        ...prev,
        contentTemplateId: templateId,
        contentGuidelines: formattedBrief
      }));
    }
  };

  // Image upload handler
  const handleCampaignImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Invalid file.', 'Please select a valid image file.', 4000);
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('File too large.', 'Image size must be less than 5MB.', 4000);
        return;
      }
      
      setCampaignImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setCampaignImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Store selection handlers
  const handleStoreSelectionTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      storeSelection: {
        ...prev.storeSelection,
        type: type,
        selectedStores: [],
        selectedRegions: [],
        excludedStores: []
      }
    }));
  };

  const handleStoreToggle = (storeId) => {
    setFormData(prev => {
      const isSelected = prev.storeSelection.selectedStores.includes(storeId);
      return {
        ...prev,
        storeSelection: {
          ...prev.storeSelection,
          selectedStores: isSelected 
            ? prev.storeSelection.selectedStores.filter(id => id !== storeId)
            : [...prev.storeSelection.selectedStores, storeId]
        }
      };
    });
  };

  const handleRegionToggle = (region) => {
    setFormData(prev => {
      const isSelected = prev.storeSelection.selectedRegions.includes(region);
      return {
        ...prev,
        storeSelection: {
          ...prev.storeSelection,
          selectedRegions: isSelected 
            ? prev.storeSelection.selectedRegions.filter(r => r !== region)
            : [...prev.storeSelection.selectedRegions, region]
        }
      };
    });
  };

  const validateStep = (step) => {
    let errors = {};
    let isValid = true;
    
    switch(step) {
      case 1:
        // Validate Basic Information
        if (!formData.name.trim()) {
          errors.name = 'Campaign name is required';
          isValid = false;
        }
        
        if (!formData.description.trim()) {
          errors.description = 'Campaign description is required';
          isValid = false;
        }
        
        if (formData.objectives.length === 0) {
          errors.objectives = 'At least one campaign objective is required';
          isValid = false;
        }
        
        if (!formData.category) {
          errors.category = 'Campaign category is required';
          isValid = false;
        }
        break;
        
      case 2:
        // Validate Campaign Details
        if (!formData.startDate) {
          errors.startDate = 'Campaign start date is required';
          isValid = false;
        }
        
        if (!formData.applicationDeadline) {
          errors.applicationDeadline = 'Application deadline is required';
          isValid = false;
        }
        
        if (!formData.reviewDeadline) {
          errors.reviewDeadline = 'Review deadline is required';
          isValid = false;
        }
        
        if (!formData.endDate) {
          errors.endDate = 'Campaign end date is required';
          isValid = false;
        }
        
        // Validate date order
        if (formData.startDate && formData.applicationDeadline && formData.startDate > formData.applicationDeadline) {
          errors.applicationDeadline = 'Application deadline must be after campaign start';
          isValid = false;
        }
        
        if (formData.applicationDeadline && formData.reviewDeadline && formData.applicationDeadline > formData.reviewDeadline) {
          errors.reviewDeadline = 'Review deadline must be after application deadline';
          isValid = false;
        }
        
        if (formData.reviewDeadline && formData.endDate && formData.reviewDeadline > formData.endDate) {
          errors.endDate = 'Campaign end must be after review deadline';
          isValid = false;
        }
        
        if (!formData.budget.trim()) {
          errors.budget = 'Budget is required';
          isValid = false;
        }
        
        if (!formData.compensationAmount.trim()) {
          errors.compensationAmount = 'Compensation details are required';
          isValid = false;
        }
        
        if ((formData.compensationType === 'product' || formData.compensationType === 'both') && !formData.productDetails.trim()) {
          errors.productDetails = 'Product details are required';
          isValid = false;
        }
        break;
        
      case 3:
        // Validate Influencer Requirements
        // These fields are optional, so no validation needed
        break;
        
      case 4:
        // Validate Content Requirements
        if (formData.contentType.length === 0) {
          errors.contentType = 'At least one content type is required';
          isValid = false;
        }
        
        if (!formData.contentGuidelines.trim()) {
          errors.contentGuidelines = 'Content guidelines are required';
          isValid = false;
        }
        break;
    }
    
    setStepErrors(errors);
    return isValid;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      setStepErrors({});
    } else {
      // Show error feedback
      showError('Missing fields.', 'Please fill in all required fields before continuing.', 5000);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleTermsAccept = (effectiveDate) => {
    setTermsAcceptedDate(effectiveDate);
    setShowTermsModal(false);
    // Continue with form submission after terms acceptance
    setTimeout(() => {
      document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, 100);
  };

  const handleTermsClose = () => {
    setShowTermsModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get CSRF token from form
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      setError('Security validation failed. Please refresh the page and try again.');
      
      // Show error feedback
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      
      return;
    }
    
    // Validate required fields
    if (!formData.name || !formData.description || !formData.category || formData.objectives.length === 0 ||
        !formData.startDate || !formData.endDate || !formData.budget || !formData.contentGuidelines || !formData.totalSpots) {
      setError('Please fill in all required fields');
      
      // Show error feedback
      showError('Missing fields.', 'Please fill in all required fields before continuing.', 5000);
      
      return;
    }
    
    // Show Terms and Conditions modal if not accepted yet
    if (!termsAcceptedDate) {
      setShowTermsModal(true);
      return;
    }
    
    // Show payment modal if commission not agreed
    if (!formData.commissionAgreed) {
      setShowPaymentModal(true);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Upload campaign image if provided
      let imageUrl = '';
      if (campaignImage) {
        setImageUploading(true);
        const imageRef = ref(storage, `campaignImages/${currentUser.uid}/${Date.now()}_${campaignImage.name}`);
        const uploadResult = await uploadBytes(imageRef, campaignImage);
        imageUrl = await getDownloadURL(uploadResult.ref);
        setImageUploading(false);
      }

      // Sanitize all form data
      const campaignData = {
        // Basic Information
        name: sanitizeText(formData.name),
        description: sanitizeHtml(formData.description),
        objectives: formData.objectives.map(obj => sanitizeText(obj)),
        category: sanitizeText(formData.category),
        imageUrl: imageUrl || '',
        
        // Campaign Details
        startDate: formData.startDate,
        applicationDeadline: formData.applicationDeadline,
        reviewDeadline: formData.reviewDeadline,
        endDate: formData.endDate,
        budget: sanitizeText(formData.budget),
        compensationType: sanitizeText(formData.compensationType),
        compensationAmount: sanitizeText(formData.compensationAmount),
        productDetails: sanitizeHtml(formData.productDetails),
        
        minFollowers: sanitizeText(formData.minFollowers),
        maxInfluencers: sanitizeText(formData.maxInfluencers),
        totalSpots: parseInt(sanitizeText(formData.totalSpots)) || 0,
        preferredNiches: formData.preferredNiches.map(niche => sanitizeText(niche)),
        requiredPlatforms: formData.requiredPlatforms.map(platform => sanitizeText(platform)),
        
        contentType: formData.contentType.map(type => sanitizeText(type)),
        contentGuidelines: sanitizeContentGuidelines(formData.contentGuidelines),
        requiredHashtags: sanitizeText(formData.requiredHashtags),
        requiredMentions: sanitizeText(formData.requiredMentions),
        
        additionalInfo: sanitizeHtml(formData.additionalInfo),
        termsAndConditions: true,
        termsAcceptedDate: termsAcceptedDate,
        
        // Store selection
        storeSelection: {
          type: sanitizeText(formData.storeSelection.type),
          selectedStores: formData.storeSelection.selectedStores.map(id => sanitizeText(id)),
          selectedRegions: formData.storeSelection.selectedRegions.map(region => sanitizeText(region)),
          excludedStores: formData.storeSelection.excludedStores.map(id => sanitizeText(id))
        }
      };
      
      // Add additional fields to campaign data
      const finalCampaignData = {
        ...campaignData,
        brandId: currentUser.uid,
        brandName: sanitizeText(userProfile?.companyName || userProfile?.businessName || 'Brand'),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'pending_payment',
        influencers: [],
        submissions: [],
        metrics: {
          reach: 0,
          engagement: 0,
          clicks: 0,
          conversions: 0
        },
        payment: {
          commission: commissionAmount,
          isPaid: false,
          totalBudget: parseFloat(campaignData.budget.replace(/[^0-9.]/g, ''))
        }
      };
      
      const docRef = await addDoc(collection(db, 'campaigns'), finalCampaignData);
      
      // Store the created campaign ID
      setCreatedCampaignId(docRef.id);
      
      // Show success feedback
      showSuccess('Campaign created successfully!', 'Your campaign has been created and is now live for influencer applications.', 4000);
      
      // Show payment instructions modal
      setShowPaymentInstructionsModal(true);
    } catch (error) {
      console.error('Error creating campaign:', error);
      setError('Failed to create campaign. Please try again.');
      
      // Show error feedback
      showError('Failed to create campaign.', 'Please check your connection and try again.', 6000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Toast Notifications */}

      
      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Payment Required</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      To proceed with your campaign, a payment of ₱{commissionAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} is required.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Your campaign will be submitted but will not be visible to influencers until payment is settled.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:col-start-2 sm:text-sm"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, commissionAgreed: true }));
                    setShowPaymentModal(false);
                    // Submit the form after closing the modal
                    setTimeout(() => {
                      document.getElementById('campaign-form').dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                    }, 100);
                  }}
                >
                  Agree & Submit
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowPaymentModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Instructions Modal */}
      <PaymentInstructionsModal
        isOpen={showPaymentInstructionsModal}
        onClose={() => {
          setShowPaymentInstructionsModal(false);
          navigate('/brand/campaigns');
        }}
        campaignId={createdCampaignId}
        commissionAmount={commissionAmount}
      />
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Create Campaign</h2>
        <p className="text-gray-600">Set up a new influencer marketing campaign</p>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}
      
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="w-full flex items-center">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${currentStep >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`}>
              1
            </div>
            <div className={`h-1 flex-1 mx-2 ${currentStep >= 2 ? 'bg-primary' : 'bg-gray-200'}`}></div>
          </div>
          <div className="w-full flex items-center">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${currentStep >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`}>
              2
            </div>
            <div className={`h-1 flex-1 mx-2 ${currentStep >= 3 ? 'bg-primary' : 'bg-gray-200'}`}></div>
          </div>
          <div className="w-full flex items-center">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${currentStep >= 3 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`}>
              3
            </div>
            <div className={`h-1 flex-1 mx-2 ${currentStep >= 4 ? 'bg-primary' : 'bg-gray-200'}`}></div>
          </div>
          <div className="w-full flex items-center">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${currentStep >= 4 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`}>
              4
            </div>
            <div className={`h-1 flex-1 mx-2 ${currentStep >= 5 ? 'bg-primary' : 'bg-gray-200'}`}></div>
          </div>
          <div className="flex items-center">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${currentStep >= 5 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`}>
              5
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <div className="text-center">Basic Info</div>
          <div className="text-center">Campaign Details</div>
          <div className="text-center">Influencer Requirements</div>
          <div className="text-center">Content Requirements</div>
          <div className="text-center">Review & Submit</div>
        </div>
      </div>
      
      {/* Form Content */}
      <div className="bg-white rounded-lg shadow p-6">
        <form id="campaign-form" onSubmit={handleSubmit}>
          <CSRFToken />
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Campaign Name*</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`mt-1 block w-full border ${stepErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                  />
                  {stepErrors.name && <p className="mt-1 text-sm text-red-600">{stepErrors.name}</p>}
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">Campaign Description*</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    required
                    className={`mt-1 block w-full border ${stepErrors.description ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                    placeholder="Describe your campaign and what you're looking for from influencers..."
                  />
                  {stepErrors.description && <p className="mt-1 text-sm text-red-600">{stepErrors.description}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Objectives*</label>
                  <div className={`space-y-2 ${stepErrors.objectives ? 'border border-red-500 p-2 rounded-md' : ''}`}>
                    {['Brand Awareness', 'Product Launch', 'Increase Sales', 'Generate Leads', 'Community Building', 'Content Creation'].map((objective) => (
                      <div key={objective} className="flex items-center">
                        <input
                          id={objective.replace(/\s+/g, '')}
                          type="checkbox"
                          checked={formData.objectives.includes(objective)}
                          onChange={() => handleArrayChange('objectives', objective)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label htmlFor={objective.replace(/\s+/g, '')} className="ml-2 block text-sm text-gray-700">
                          {objective}
                        </label>
                      </div>
                    ))}
                  </div>
                  {stepErrors.objectives && <p className="mt-1 text-sm text-red-600">{stepErrors.objectives}</p>}
                </div>
                
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">Campaign Category*</label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleChange({ target: { name: 'category', value } })}
                  >
                    <SelectTrigger id="category" error={!!stepErrors.category} className="mt-1 w-full">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fashion">Fashion</SelectItem>
                      <SelectItem value="Beauty">Beauty</SelectItem>
                      <SelectItem value="Food & Beverage">Food & Beverage</SelectItem>
                      <SelectItem value="Health & Fitness">Health & Fitness</SelectItem>
                      <SelectItem value="Technology">Technology</SelectItem>
                      <SelectItem value="Travel">Travel</SelectItem>
                      <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                      <SelectItem value="Entertainment">Entertainment</SelectItem>
                      <SelectItem value="Home & Decor">Home & Decor</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {stepErrors.category && <p className="mt-1 text-sm text-red-600">{stepErrors.category}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Image</label>
                  <p className="text-xs text-gray-500 mb-3">Recommended size: 800x400 pixels (16:9 ratio). Maximum file size: 5MB</p>
                  
                  {campaignImagePreview ? (
                    <div className="mb-4">
                      <div className="relative inline-block">
                        <img
                          src={campaignImagePreview}
                          alt="Campaign preview"
                          className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setCampaignImage(null);
                            setCampaignImagePreview(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition duration-200"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition duration-200">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="mt-4">
                          <label htmlFor="campaignImage" className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-gray-900">
                              Upload campaign image
                            </span>
                            <span className="mt-1 block text-xs text-gray-500">
                              PNG, JPG, GIF up to 5MB
                            </span>
                          </label>
                          <input
                            id="campaignImage"
                            name="campaignImage"
                            type="file"
                            accept="image/*"
                            onChange={handleCampaignImageChange}
                            className="sr-only"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!campaignImagePreview && (
                    <button
                      type="button"
                      onClick={() => document.getElementById('campaignImage').click()}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Choose Image
                    </button>
                  )}
                  
                  {imageUploading && (
                    <div className="mt-2 flex items-center text-sm text-gray-600">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Uploading image...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {currentStep === 2 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Details</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">Campaign Start*</label>
                    <DatePicker
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={(value) => handleChange({ target: { name: 'startDate', value } })}
                      error={!!stepErrors.startDate}
                      placeholder="Pick a date"
                      className="mt-1"
                    />
                    {stepErrors.startDate && <p className="mt-1 text-sm text-red-600">{stepErrors.startDate}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="applicationDeadline" className="block text-sm font-medium text-gray-700">Application Deadline*</label>
                    <DatePicker
                      id="applicationDeadline"
                      name="applicationDeadline"
                      value={formData.applicationDeadline}
                      onChange={(value) => handleChange({ target: { name: 'applicationDeadline', value } })}
                      error={!!stepErrors.applicationDeadline}
                      placeholder="Pick a date"
                      className="mt-1"
                    />
                    {stepErrors.applicationDeadline && <p className="mt-1 text-sm text-red-600">{stepErrors.applicationDeadline}</p>}
                  </div>

                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="reviewDeadline" className="block text-sm font-medium text-gray-700">Review Deadline*</label>
                    <DatePicker
                      id="reviewDeadline"
                      name="reviewDeadline"
                      value={formData.reviewDeadline}
                      onChange={(value) => handleChange({ target: { name: 'reviewDeadline', value } })}
                      max={formData.endDate || undefined}
                      error={!!stepErrors.reviewDeadline}
                      placeholder="Pick a date"
                      className="mt-1"
                    />
                    {stepErrors.reviewDeadline && <p className="mt-1 text-sm text-red-600">{stepErrors.reviewDeadline}</p>}
                    <p className="mt-1 text-xs text-gray-500">Must be on or before the campaign end date</p>
                  </div>
                  
                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">Campaign End*</label>
                    <DatePicker
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={(value) => handleChange({ target: { name: 'endDate', value } })}
                      min={formData.applicationDeadline || undefined}
                      error={!!stepErrors.endDate}
                      placeholder="Pick a date"
                      className="mt-1"
                    />
                    {stepErrors.endDate && <p className="mt-1 text-sm text-red-600">{stepErrors.endDate}</p>}
                    <p className="mt-1 text-xs text-gray-500">Final date for campaign completion</p>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700">Total Budget (PHP)*</label>
                  <input
                    type="text"
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      const formattedValue = value ? parseFloat(value).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2}) : '';
                      handleChange({target: {name: 'budget', value: formattedValue}});
                    }}
                    required
                    className={`mt-1 block w-full border ${stepErrors.budget ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                    placeholder="50,000"
                  />
                  {stepErrors.budget && <p className="mt-1 text-sm text-red-600">{stepErrors.budget}</p>}
                  {formData.budget && !isNaN(parseFloat(formData.budget.replace(/[^0-9.]/g, ''))) && (
                    <p className="mt-1 text-sm text-gray-500">
                      Initial Payment: PHP {commissionAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="compensationType" className="block text-sm font-medium text-gray-700">Compensation Type*</label>
                  <Select
                    value={formData.compensationType}
                    onValueChange={(value) => handleChange({ target: { name: 'compensationType', value } })}
                  >
                    <SelectTrigger id="compensationType" className="mt-1 w-full">
                      <SelectValue placeholder="Select compensation type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monetary">Monetary</SelectItem>
                      <SelectItem value="product">Product</SelectItem>
                      <SelectItem value="both">Both (Monetary + Product)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label htmlFor="compensationAmount" className="block text-sm font-medium text-gray-700">Compensation Details (PHP)*</label>
                  <input
                    type="text"
                    id="compensationAmount"
                    name="compensationAmount"
                    value={formData.compensationAmount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      const formattedValue = value ? parseFloat(value).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2}) : '';
                      handleChange({target: {name: 'compensationAmount', value: formattedValue}});
                    }}
                    required
                    className={`mt-1 block w-full border ${stepErrors.compensationAmount ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                    placeholder="5,000"
                  />
                  {stepErrors.compensationAmount && <p className="mt-1 text-sm text-red-600">{stepErrors.compensationAmount}</p>}
                  <p className="mt-1 text-xs text-gray-500">Amount per influencer (e.g. per post, per review)</p>
                </div>
                
                {formData.compensationType === 'product' || formData.compensationType === 'both' ? (
                  <div>
                    <label htmlFor="productDetails" className="block text-sm font-medium text-gray-700">Product Details*</label>
                    <textarea
                      id="productDetails"
                      name="productDetails"
                      value={formData.productDetails}
                      onChange={handleChange}
                      rows={3}
                      className={`mt-1 block w-full border ${stepErrors.productDetails ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                      placeholder="Describe the product(s) you'll be providing to influencers..."
                    />
                    {stepErrors.productDetails && <p className="mt-1 text-sm text-red-600">{stepErrors.productDetails}</p>}
                  </div>
                ) : null}
                
                {/* Store Selection Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Campaign Store Coverage</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-base font-medium text-gray-900">Select store coverage for this campaign</label>
                      <p className="text-sm leading-5 text-gray-500">Choose which stores this campaign will apply to</p>
                      <fieldset className="mt-4">
                        <legend className="sr-only">Store coverage options</legend>
                        <div className="space-y-4">
                          <div className="flex items-center">
                            <input
                              id="all-stores"
                              name="store-coverage"
                              type="radio"
                              checked={formData.storeSelection.type === 'all'}
                              onChange={() => handleStoreSelectionTypeChange('all')}
                              className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                            />
                            <label htmlFor="all-stores" className="ml-3 block text-sm font-medium text-gray-700">
                              All Stores
                              <span className="block text-sm text-gray-500">Campaign applies to all your store locations</span>
                            </label>
                          </div>
                          
                          <div className="flex items-center">
                            <input
                              id="specific-stores"
                              name="store-coverage"
                              type="radio"
                              checked={formData.storeSelection.type === 'specific'}
                              onChange={() => handleStoreSelectionTypeChange('specific')}
                              className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                            />
                            <label htmlFor="specific-stores" className="ml-3 block text-sm font-medium text-gray-700">
                              Specific Stores
                              <span className="block text-sm text-gray-500">Choose individual store locations</span>
                            </label>
                          </div>
                          
                          <div className="flex items-center">
                            <input
                              id="regional-stores"
                              name="store-coverage"
                              type="radio"
                              checked={formData.storeSelection.type === 'regional'}
                              onChange={() => handleStoreSelectionTypeChange('regional')}
                              className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                            />
                            <label htmlFor="regional-stores" className="ml-3 block text-sm font-medium text-gray-700">
                              Regional Coverage
                              <span className="block text-sm text-gray-500">Select by regions or cities</span>
                            </label>
                          </div>
                        </div>
                      </fieldset>
                    </div>
                    
                    {/* Conditional Store Selection UI */}
                    {formData.storeSelection.type === 'specific' && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-900 mb-3">Select Specific Stores</h5>
                        {brandStores && brandStores.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {brandStores.map((store) => (
                              <div key={store.id} className="flex items-center">
                                <input
                                  id={`store-${store.id}`}
                                  type="checkbox"
                                  checked={formData.storeSelection.selectedStores.includes(store.id)}
                                  onChange={() => handleStoreToggle(store.id)}
                                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                />
                                <label htmlFor={`store-${store.id}`} className="ml-2 block text-sm text-gray-700">
                                  <span className="font-medium">{store.name}</span>
                                  <span className="block text-xs text-gray-500">{store.region} - {store.city}</span>
                                  <span className="block text-xs text-gray-500">{store.address}</span>
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No stores available</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Please add store locations in your Profile → Store Locations first.
                            </p>
                            <div className="mt-6">
                              <button
                                type="button"
                                onClick={() => window.open('/brand/profile', '_blank')}
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                              >
                                Manage Stores
                              </button>
                            </div>
                          </div>
                        )}
                        {formData.storeSelection.selectedStores.length > 0 && (
                          <div className="mt-3 text-sm text-gray-600">
                            Selected: {formData.storeSelection.selectedStores.length} store(s)
                          </div>
                        )}
                      </div>
                    )}
                    
                    {formData.storeSelection.type === 'regional' && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-900 mb-3">Select Regions</h5>
                        {brandStores && brandStores.length > 0 ? (
                          <>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {/* Get unique regions from brand stores */}
                              {Array.from(new Set(brandStores.map(store => store.region)))
                                .filter(region => region && region.trim() !== '')
                                .sort()
                                .map(region => (
                                <div key={region} className="flex items-center">
                                  <input
                                    id={`region-${region}`}
                                    type="checkbox"
                                    checked={formData.storeSelection.selectedRegions.includes(region)}
                                    onChange={() => handleRegionToggle(region)}
                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                  />
                                  <label htmlFor={`region-${region}`} className="ml-2 block text-sm text-gray-700">
                                    {region}
                                    <span className="block text-xs text-gray-500">
                                      {brandStores.filter(store => store.region === region).length} store(s)
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </div>
                            {formData.storeSelection.selectedRegions.length > 0 && (
                              <div className="mt-3 text-sm text-gray-600">
                                Selected: {formData.storeSelection.selectedRegions.length} region(s) covering 
                                {brandStores.filter(store => 
                                  formData.storeSelection.selectedRegions.includes(store.region)
                                ).length} store(s)
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center py-8">
                            <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No regions available</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Please add store locations with regions in your Profile → Store Locations first.
                            </p>
                            <div className="mt-6">
                              <button
                                type="button"
                                onClick={() => window.open('/brand/profile', '_blank')}
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                              >
                                Manage Stores
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {formData.storeSelection.type === 'all' && (
                      <div className="mt-4 p-4 bg-green-50 rounded-lg">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">All stores selected</h3>
                            <div className="mt-2 text-sm text-green-700">
                              <p>This campaign will be available for all your store locations.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {currentStep === 3 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Influencer Requirements</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="minFollowers" className="block text-sm font-medium text-gray-700">Minimum Followers</label>
                    <input
                      type="number"
                      id="minFollowers"
                      name="minFollowers"
                      value={formData.minFollowers}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="e.g. 5000"
                    />
                    <p className="mt-1 text-xs text-gray-500">Minimum follower count required</p>
                  </div>
                  
                  <div>
                    <label htmlFor="maxInfluencers" className="block text-sm font-medium text-gray-700">Maximum Followers</label>
                    <input
                      type="number"
                      id="maxInfluencers"
                      name="maxInfluencers"
                      value={formData.maxInfluencers}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="e.g. 100000"
                    />
                    <p className="mt-1 text-xs text-gray-500">Maximum follower count (optional)</p>
                  </div>
                  
                  <div>
                    <label htmlFor="totalSpots" className="block text-sm font-medium text-gray-700">Number of Influencers Needed *</label>
                    <input
                      type="number"
                      id="totalSpots"
                      name="totalSpots"
                      value={formData.totalSpots}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="e.g. 10"
                      min="1"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">How many influencers do you need?</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Niches</label>
                  <div className="space-y-2">
                    {['Fashion', 'Beauty', 'Lifestyle', 'Travel', 'Food', 'Fitness', 'Technology', 'Gaming', 'Entertainment', 'Business', 'Education', 'Parenting'].map((niche) => (
                      <div key={niche} className="inline-flex items-center mr-4 mb-2">
                        <input
                          id={`niche-${niche}`}
                          type="checkbox"
                          checked={formData.preferredNiches.includes(niche)}
                          onChange={() => handleArrayChange('preferredNiches', niche)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label htmlFor={`niche-${niche}`} className="ml-2 block text-sm text-gray-700">
                          {niche}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Required Platforms</label>
                  <div className="space-y-2">
                    {['Instagram', 'TikTok', 'YouTube', 'Facebook', 'X', 'Blog'].map((platform) => (
                      <div key={platform} className="inline-flex items-center mr-4 mb-2">
                        <input
                          id={`platform-${platform}`}
                          type="checkbox"
                          checked={formData.requiredPlatforms.includes(platform)}
                          onChange={() => handleArrayChange('requiredPlatforms', platform)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label htmlFor={`platform-${platform}`} className="ml-2 block text-sm text-gray-700">
                          {platform}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {currentStep === 4 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Content Requirements</h3>
              <div className="space-y-6">
                {/* Template Selector */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <TemplateSelector
                    onTemplateSelect={handleTemplateSelect}
                    selectedTemplateId={selectedTemplateId}
                    brandInfo={{
                      brandName: userProfile?.businessName || formData.name,
                      brandHashtag: formData.requiredHashtags?.split(' ')[0] || '#YourBrand',
                      brandHandle: formData.requiredMentions?.split(' ')[0] || '@yourbrand',
                      productName: formData.name
                    }}
                  />
                </div>

                {/* Content Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content Type*</label>
                  <div className={`space-y-2 ${stepErrors.contentType ? 'border border-red-500 p-2 rounded-md' : ''}`}>
                    {['Photo', 'Video', 'Reel', 'Story', 'Blog Post', 'Review', 'Unboxing', 'Tutorial'].map((type) => (
                      <div key={type} className="inline-flex items-center mr-4 mb-2">
                        <input
                          id={`content-${type}`}
                          type="checkbox"
                          checked={formData.contentType.includes(type)}
                          onChange={() => handleArrayChange('contentType', type)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label htmlFor={`content-${type}`} className="ml-2 block text-sm text-gray-700">
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>
                  {stepErrors.contentType && <p className="mt-1 text-sm text-red-600">{stepErrors.contentType}</p>}
                </div>
                
                {/* Content Guidelines - Auto-filled from template or custom */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="contentGuidelines" className="block text-sm font-medium text-gray-700">
                      Content Guidelines*
                    </label>
                    {selectedTemplateId && selectedTemplateId !== 'custom' && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        ✓ Auto-filled from template
                      </span>
                    )}
                  </div>
                  <textarea
                    id="contentGuidelines"
                    name="contentGuidelines"
                    value={formData.contentGuidelines}
                    onChange={handleChange}
                    rows={12}
                    required
                    className={`mt-1 block w-full border ${stepErrors.contentGuidelines ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono text-xs`}
                    placeholder="Select a template above or write your own content guidelines..."
                  />
                  {stepErrors.contentGuidelines && <p className="mt-1 text-sm text-red-600">{stepErrors.contentGuidelines}</p>}
                  <p className="mt-1 text-xs text-gray-500">
                    {selectedTemplateId && selectedTemplateId !== 'custom' 
                      ? 'You can customize this template-generated brief as needed'
                      : 'Describe what you want influencers to include in their content'}
                  </p>
                </div>
                
                {/* Required Hashtags */}
                <div>
                  <label htmlFor="requiredHashtags" className="block text-sm font-medium text-gray-700">Required Hashtags</label>
                  <input
                    type="text"
                    id="requiredHashtags"
                    name="requiredHashtags"
                    value={formData.requiredHashtags}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="e.g. #BrandName #CampaignName (separate with spaces)"
                  />
                  <p className="mt-1 text-xs text-gray-500">These hashtags will be required in all influencer posts</p>
                </div>
                
                {/* Required Mentions */}
                <div>
                  <label htmlFor="requiredMentions" className="block text-sm font-medium text-gray-700">Required Mentions</label>
                  <input
                    type="text"
                    id="requiredMentions"
                    name="requiredMentions"
                    value={formData.requiredMentions}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="e.g. @brandhandle (separate with spaces)"
                  />
                  <p className="mt-1 text-xs text-gray-500">Brand accounts that must be tagged in posts</p>
                </div>

                {/* Optional: Add Example URLs */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h5 className="text-sm font-medium text-blue-900 mb-1">
                          Optional: Add Your Own Example Posts
                        </h5>
                        <p className="text-sm text-blue-700 mb-2">
                          Want to show influencers specific examples? You can add URLs to example posts here (optional).
                        </p>
                        <p className="text-xs text-blue-600">
                          Coming soon: Add example post URLs to guide influencers
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {currentStep === 5 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Review & Submit</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700">Additional Information</label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Any additional information or special instructions for influencers..."
                  />
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-sm border border-blue-100">
                  <h4 className="text-xl font-bold text-primary mb-4 text-center">Your Campaign at a Glance ✨</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Campaign Name</p>
                      <p className="text-lg font-medium text-gray-800">{formData.name}</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Category</p>
                      <p className="text-lg font-medium text-gray-800">{formData.category}</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Campaign Dates</p>
                      <p className="text-lg font-medium text-gray-800">{formData.startDate} → {formData.endDate}</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Content Types</p>
                      <p className="text-lg font-medium text-gray-800">{formData.contentType.join(', ') || 'None specified'}</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Your Budget</p>
                      <p className="text-lg font-medium text-gray-800">₱{formData.budget.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Compensation Type</p>
                      <p className="text-lg font-medium text-gray-800">{formData.compensationType === 'monetary' ? 'Monetary' : formData.compensationType === 'product' ? 'Product' : 'Both'}</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Store Coverage</p>
                      <p className="text-lg font-medium text-gray-800">
                        {formData.storeSelection.type === 'all' && 'All Stores'}
                        {formData.storeSelection.type === 'specific' && `${formData.storeSelection.selectedStores.length} Specific Stores`}
                        {formData.storeSelection.type === 'regional' && `${formData.storeSelection.selectedRegions.length} Regions`}
                      </p>
                      {formData.storeSelection.type === 'regional' && formData.storeSelection.selectedRegions.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">{formData.storeSelection.selectedRegions.join(', ')}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-4 bg-primary bg-opacity-10 p-3 rounded-md border border-primary border-opacity-20">
                    <p className="text-sm text-gray-500 mb-1">Initial Payment (70%)</p>
                    <p className="text-xl font-bold text-primary">₱{commissionAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    <p className="text-xs text-gray-600 mt-1">Remaining 30% paid after campaign completion</p>
                  </div>
                </div>
                
                <div className="rounded-md border border-gray-300 p-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="termsAndConditions"
                        name="termsAndConditions"
                        type="checkbox"
                        checked={formData.termsAndConditions}
                        onChange={handleChange}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="termsAndConditions" className="font-medium text-gray-700">Terms and Conditions*</label>
                      <p className="text-gray-500">I agree to Engaged Manila's terms and conditions for campaign creation. I confirm that all information provided is accurate and I have the authority to create this campaign.</p>
                    </div>
                  </div>
                </div>
                
                {error && (
                  <div className="text-red-600 text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Previous
              </button>
            )}
            
            <div className="flex-1"></div>
            
            {currentStep < 5 ? (
              <button
                type="button"
                onClick={nextStep}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="xs" color="white" />
                    <span className="ml-2">Creating...</span>
                  </>
                ) : (
                  'Create Campaign'
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={handleTermsClose}
        onAccept={handleTermsAccept}
        title="Campaign Creation Terms and Conditions"
        acceptButtonText="Accept and Create Campaign"
      />
    </div>
  );
};

export default CreateCampaign;
